import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ScheduleReservationPop from './Modal/ScheduleReservationPop';
import { RootState } from '../../store/reducers';
import { ContentsInfo, FrameInfo, GetScheduleType } from '../../store/reducers/schedule/scheduleGetReducer';
import {
  GET_SCHEDULE_REQUEST,
  GET_SCHEDULE_RESERVATION_CONFIRM_INITIAL,
  GET_SCHEDULE_RESERVATION_CONFIRM_REQUEST,
  GET_SCHEDULE_RESERVATION_DISPATCH,
} from '../../store/actions';
import { stateScheduleReservation } from '../../store/reducers/schedule/scheduleReservationStateReducer';
import Header from '../../components/Layouts/MyPage/Header/MyPageHeader.index';
import Footer from '../../components/Layouts/MyPage/Footer/MyPageFooter.index';

import './style/Schedule.scss';
import './style/_button.scss';
import './style/ScheduleModal.scss';
import * as types from '../../store/actions/shopList/shopActionTypes';
import CustomLoading from '../../components/Common/Loading/CustomLoading.index';
import { ReservationConfirmData } from '../../store/reducers/schedule/reservationConfirmReducer';

const Schedule: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const SCHEDULE_RESERVATION_CACHE_KEY = 'scheduleReservationCache';
  const SCHEDULE_RESERVATION_POP_CACHE_KEY = 'scheduleReservationPopCache';
  // 初回マウント判定とスキップフラグ（selectFlag による自動検索抑制用）
  const isInitialMount = useRef(true);
  const [isAllNoData, setIsAllNoData] = useState(true);
  const [programComment, setProgramComment] = useState('');

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [paramShopId] = React.useState(() => params.get('shop_id'));
  const [paramShopName] = React.useState(() => params.get('shop_name'));

  /**
   * 日付フォーマット取得ユーティリティ
   * formatType:
   *  1 = 今日（YYYYMMDD）
   *  2 = 今日から -7日（YYYYMMDD）
   *  3 = 今日から +7日（YYYYMMDD）
   * fromDateStr を渡すとその日付を基準に計算する
   */
  const formatDate = (formatType: number, fromDateStr?: string | null) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const JST_OFFSET = 9 * 60 * 60 * 1000;

    const getJSTDate = (date?: Date) => {
      const base = date ? date.getTime() : Date.now();
      return new Date(base + JST_OFFSET);
    };

    let targetDate: Date;

    if (!fromDateStr) {
      targetDate = getJSTDate();
    } else {
      const year = Number(fromDateStr.slice(0, 4));
      const month = Number(fromDateStr.slice(4, 6)) - 1;
      const day = Number(fromDateStr.slice(6, 8));

      targetDate = getJSTDate(new Date(Date.UTC(year, month, day)));
    }

    if (formatType === 1) {
      // 当前 JST 日付
      targetDate = getJSTDate();
    } else if (formatType === 2) {
      // JST - 7 日付
      targetDate.setUTCDate(targetDate.getUTCDate() - 7);
    } else if (formatType === 3) {
      // JST + 7 日付
      targetDate.setUTCDate(targetDate.getUTCDate() + 7);
    }

    const yyyy = targetDate.getUTCFullYear();
    const mm = pad(targetDate.getUTCMonth() + 1);
    const dd = pad(targetDate.getUTCDate());

    return `${yyyy}${mm}${dd}`;
  };

  /**
   * 表示用日付フォーマット YYYYMMDD -> MM/DD
   */
  const formatShowDate = (date: string | null) => {
    if (!date || date.length !== 8) return date;
    const mm = date.substring(4, 6);
    const dd = date.substring(6, 8);
    return `${mm}/${dd}`;
  };

  /**
   * 表示用時間フォーマット HHMM -> HH:MM
   * 無効な場合はそのまま返す
   */
  const formatShowTime = (time: string | null) => {
    if (!time || time.length !== 4) return time;
    const hh = parseInt(time.substring(0, 2), 10);
    const mm = parseInt(time.substring(2, 4), 10);
    return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
  };

  const [reservationPopModal, setReservationPopModal] = useState(false);

  const { isGetScheduleLoaded, getSchedule } = useSelector<RootState, GetScheduleType>(
    (state) => state.schedule.scheduleGet,
  );

  const { isGetReservationConfirmLoading, isGetReservationConfirmLoaded } = useSelector<
    RootState,
    ReservationConfirmData
  >((state) => state.schedule.getReservationConfirmReducer);

  const { scheduleDispatch } = useSelector<RootState, stateScheduleReservation>(
    (state) => state.schedule.scheduleReservationState,
  );

  const { shop, loaded } = useSelector((state: RootState) => state.shop);

  const [preOpenDay, setPreOpenDay] = useState(
    scheduleDispatch.imageDate != null && scheduleDispatch.imageDate > formatDate(1)
      ? scheduleDispatch.imageDate
      : formatDate(1),
  );

  // --- 1. 添加 Ref 引用 ---
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [queryParam, setQueryParam] = useState<{
    clubCode: number | null;
    programId: string | null;
    imageDate: string | null;
    isSkipSearch: boolean;
  }>(() => {
    if (paramShopId && paramShopId.length > 0) {
      const paramShopIdNum = Number(paramShopId);
      if (Number.isNaN(paramShopIdNum) || !Number.isInteger(paramShopIdNum) || paramShopIdNum <= 0) {
        history.push('/');
        return {
          clubCode: 0,
          programId: null,
          imageDate: formatDate(1),
          isSkipSearch: true,
        };
      }
      return {
        clubCode: Number(paramShopId),
        programId: null,
        imageDate: preOpenDay > formatDate(1) ? preOpenDay : formatDate(1),
        isSkipSearch: false,
      };
    }

    if (!scheduleDispatch.clubId) {
      const cacheClubCode = sessionStorage.getItem('scheduleQueryParam_clubCode');
      if (cacheClubCode) {
        const cacheProgramId = sessionStorage.getItem('scheduleQueryParam_programId');
        const cacheImageDate = sessionStorage.getItem('scheduleQueryParam_imageDate');
        return {
          clubCode: Number(cacheClubCode),
          programId: cacheProgramId ?? null,
          imageDate: cacheImageDate ?? formatDate(1),
          isSkipSearch: false,
        };
      }
      history.push('/');
      return {
        clubCode: 0,
        programId: null,
        imageDate: formatDate(1),
        isSkipSearch: true,
      };
    }

    return {
      clubCode: scheduleDispatch.clubId,
      programId: scheduleDispatch.programId ?? null,
      imageDate: scheduleDispatch.imageDate ?? formatDate(1),
      isSkipSearch: false,
    };
  });

  useEffect(() => {
    sessionStorage.setItem('scheduleQueryParam_clubCode', String(queryParam.clubCode ?? ''));
    sessionStorage.setItem('scheduleQueryParam_programId', queryParam.programId ?? '');
    sessionStorage.setItem('scheduleQueryParam_imageDate', queryParam.imageDate ?? '');
  }, [queryParam]);

  /**
   * スケジュール検索を実行する共通関数
   */
  const searchSchedule = useCallback(() => {
    setProgramComment('');
    if (queryParam.clubCode) {
      dispatch({
        type: GET_SCHEDULE_REQUEST,
        clubCode: queryParam.clubCode,
        imageDate: queryParam.imageDate,
        programId: queryParam.programId,
        history,
      });
    }
  }, [dispatch, history, queryParam]);

  /**
   * queryParam の変更に応じて検索を実行するエフェクト
   * 初回マウントや skipSearch 時は検索を抑制する
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (queryParam.isSkipSearch) {
      return;
    }
    if (queryParam.clubCode) {
      searchSchedule();
    }
  }, [queryParam, searchSchedule]);

  useEffect(() => {
    dispatch({
      type: types.GET_VISITOR_CLUB_DETAIL_INITIAL,
    });
    if (queryParam.clubCode) {
      dispatch({
        type: types.GET_VISITOR_CLUB_DETAIL_REQUEST,
        clubId: queryParam.clubCode,
      });
    }
  }, [dispatch, queryParam.clubCode]);

  useEffect(() => {
    if (loaded) {
      if (!shop) {
        if (paramShopName && paramShopName.length > 0) {
          window.location.href = `https://shop.mirafitness.jp/detail/${paramShopName}`;
        } else {
          window.location.href = 'https://shop.mirafitness.jp';
        }
      } else if (shop) {
        const webRegistrationOpenDay = shop?.webRegistrationOpenDay;
        const todayStr = formatDate(1);
        if (Number(webRegistrationOpenDay) > Number(todayStr)) {
          if (paramShopName && paramShopName.length > 0) {
            window.location.href = `https://shop.mirafitness.jp/detail/${paramShopName}`;
          } else {
            window.location.href = 'https://shop.mirafitness.jp';
          }
        } else {
          const newPreOpenDay = shop?.preOpenDay;
          const targetImageDate = newPreOpenDay != null && newPreOpenDay > todayStr ? newPreOpenDay : todayStr;
          setPreOpenDay(targetImageDate);

          if (paramShopId && paramShopId.length > 0) {
            setQueryParam((prev) => ({
              ...prev,
              imageDate: targetImageDate,
              isSkipSearch: false,
            }));
          }
        }
      }
    }
  }, [shop, loaded, history, paramShopId, paramShopName]);

  useEffect(() => {
    if (isGetReservationConfirmLoaded) {
      setReservationPopModal(true);
    }
  }, [isGetReservationConfirmLoaded]);

  const openReservationPop = useCallback(
    (
      day: string,
      week: string,
      contentsInfo: ContentsInfo,
      frameInfo: FrameInfo,
      shopName: string,
      isDisabled: boolean,
      selectEqId: number,
      selectCompanionNum: number,
    ) => {
      if (isDisabled) {
        return;
      }
      if (frameInfo.bookedCount >= frameInfo.totalCount && contentsInfo.cancelWaitNotice === 0) {
        console.log('キャンセル待ち不可');
        return;
      }
      const cacheObj = {
        contentsInfo,
        frameInfo,
        clubName: shopName,
        infoDate: day,
        week,
      };
      const jsonString = JSON.stringify(cacheObj);
      sessionStorage.setItem(SCHEDULE_RESERVATION_CACHE_KEY, jsonString);
      dispatch({
        type: GET_SCHEDULE_RESERVATION_CONFIRM_REQUEST,
        contentsId: contentsInfo.contentsId,
        clubId: queryParam.clubCode,
        contentsDate: day,
        startTime: frameInfo.startTime,
        endTime: frameInfo.endTime,
        history,
      });
      dispatch({
        type: GET_SCHEDULE_RESERVATION_DISPATCH,
        contentsId: contentsInfo.contentsId,
        clubId: queryParam.clubCode,
        clubName: shopName,
        infoDate: day,
        contentStartTime: frameInfo.startTime,
        contentEndTime: frameInfo.endTime,
        week,
        selectEqId,
        selectCompanionNum,
        history,
      });
    },
    [dispatch, history, queryParam.clubCode],
  );

  const forwardBack = useCallback(() => {
    history.push('/');
  }, [history]);

  const backToShopPage = useCallback(() => {
    history.push('/');
  }, [history]);

  /**
   * プログラム選択変更ハンドラ
   */
  const onChangeProgramId = useCallback((eve: React.ChangeEvent<HTMLInputElement>) => {
    const newProgramId = eve.target.value;
    setQueryParam((prev) => ({ ...prev, programId: newProgramId, isSkipSearch: false }));
  }, []);

  /**
   * プログラム選択変更ハンドラ
   */
  const onChangeSelectProgramId = useCallback((eve: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgramId = eve.target.value;
    setQueryParam((prev) => ({ ...prev, programId: newProgramId, isSkipSearch: false }));
  }, []);

  /**
   * 週移動ボタンから表示日を変更する
   * changType: 1=今週, 2=前の週, 3=次の週
   */
  const changeSearchDay = useCallback(
    (changType: number) => {
      let searchDay = formatDate(changType, queryParam.imageDate);
      const today = formatDate(1);
      if (changType === 1) {
        if (preOpenDay > today) {
          searchDay = preOpenDay;
        } else {
          searchDay = today;
        }
      }
      if (searchDay >= today) {
        setQueryParam((prev) => ({ ...prev, imageDate: searchDay, isSkipSearch: false }));
      }
    },
    [queryParam.imageDate, preOpenDay],
  );

  /**
   * 初期データ取得エフェクト
   * - クラブ一覧を取得
   * - clubCode が設定されていればスケジュールを取得
   */
  useEffect(() => {
    setReservationPopModal(false);
    dispatch({
      type: GET_SCHEDULE_RESERVATION_CONFIRM_INITIAL,
      history,
    });
    window.scrollTo(0, 0);
    setProgramComment('');
    if (queryParam.clubCode) {
      dispatch({
        type: GET_SCHEDULE_REQUEST,
        clubCode: queryParam.clubCode,
        imageDate: queryParam.imageDate,
        programId: queryParam.programId,
        history,
      });
    }
  }, [history, dispatch]);

  useEffect(() => {
    if (scheduleDispatch.showPopFlag) {
      const jsonString = sessionStorage.getItem(SCHEDULE_RESERVATION_CACHE_KEY);
      if (jsonString) {
        const cacheObj = JSON.parse(jsonString);
        try {
          if (
            !cacheObj.infoDate ||
            !cacheObj.week ||
            !cacheObj.contentsInfo ||
            !cacheObj.frameInfo ||
            !cacheObj.clubName
          ) {
            sessionStorage.removeItem(SCHEDULE_RESERVATION_CACHE_KEY);
            return;
          }
          let selectCacheEqId = 0;
          let selectCacheCompanionNum = 0;
          const selectInfoJsonString = sessionStorage.getItem(SCHEDULE_RESERVATION_POP_CACHE_KEY);
          if (selectInfoJsonString) {
            const cacheSelectObj = JSON.parse(selectInfoJsonString);
            selectCacheEqId = cacheSelectObj.selectEqId || 0;
            selectCacheCompanionNum = cacheSelectObj.companionNum || 0;
          }
          openReservationPop(
            cacheObj.infoDate,
            cacheObj.week,
            cacheObj.contentsInfo,
            cacheObj.frameInfo,
            cacheObj.clubName,
            false,
            selectCacheEqId,
            selectCacheCompanionNum,
          );
        } catch (error) {
          sessionStorage.removeItem(SCHEDULE_RESERVATION_CACHE_KEY);
          console.error('open openReservationPop error.', error);
        }
      }
    } else {
      sessionStorage.removeItem(SCHEDULE_RESERVATION_CACHE_KEY);
    }
  }, [dispatch, openReservationPop, scheduleDispatch.showPopFlag]);

  /**
   * GET_SCHEDULE の結果から programList を確認し、selectFlag のあるプログラムを初期選択する
   * 既に自動検索フラグがセットされた場合は次の検索をスキップするための制御を行う
   */
  useEffect(() => {
    if (isGetScheduleLoaded && getSchedule?.programList?.length > 0) {
      if (queryParam.clubCode) {
        getSchedule.programList.map((program) => {
          if (program.selectFlag === 1) {
            setQueryParam((prev) => ({ ...prev, programId: program.programId, isSkipSearch: true }));
            setProgramComment(program.programComment);
          }
          return program;
        });
      }
    }
    if (!(getSchedule.scheduleList && getSchedule.scheduleList.length > 0)) {
      return;
    }
    let allNoData = true;
    for (let i = 0; i < getSchedule.scheduleList.length; i += 1) {
      const scheduleInfo = getSchedule.scheduleList[i];
      if (scheduleInfo.contentsList && scheduleInfo.contentsList.length > 0) {
        allNoData = false;
        break;
      }
    }
    setIsAllNoData(allNoData);
  }, [isGetScheduleLoaded, getSchedule]);

  useEffect(() => {
    // 只有当数据加载完成且 DOM 渲染后才执行
    if (!isGetScheduleLoaded || !getSchedule.scheduleList) return;

    const scrollArea = document.querySelector('.p-scroll-sticky-area');
    const dayColumns = document.querySelectorAll<HTMLElement>('.p-schedule-grid__day-column');

    // 【Chrome対策】URLバーの影響を検知する透明なトラッカーを設置
    const safeTracker = document.createElement('div');
    // CSSテキストで一括設定（Safari用のプレフィックス含む）
    safeTracker.style.cssText =
      'position: -webkit-sticky; position: sticky; top: 0px; height: 0px; width: 100%; visibility: hidden; pointer-events: none; z-index: -1;';
    // ページの一番上に挿入
    const appRoot = document.getElementById('__next') || document.getElementById('root') || document.body;
    appRoot.prepend(safeTracker);

    // クローン（身代わり）を格納する配列
    const stickyItems: Array<{
      type: 'day' | 'slot';
      original: HTMLElement;
      clone: HTMLElement;
      container: HTMLElement;
      parentDayHeader?: HTMLElement | null;
    }> = [];

    // 1. 本物のヘッダーを複製（クローン）して準備する
    dayColumns.forEach((col) => {
      // --- 日付ヘッダーの複製 ---
      const dayHeader = col.querySelector<HTMLElement>('.p-schedule-grid__day-header');
      if (dayHeader) {
        const clone = dayHeader.cloneNode(true) as HTMLElement;
        clone.style.position = 'fixed';
        clone.style.visibility = 'hidden'; // 初期は隠す
        clone.style.zIndex = '50';
        clone.style.margin = '0';
        clone.setAttribute('aria-hidden', 'true');
        document.body.appendChild(clone);

        stickyItems.push({
          type: 'day',
          original: dayHeader,
          clone,
          container: col,
        });
      }

      // --- タイムスロットヘッダーの複製 ---
      const slotCols = col.querySelectorAll<HTMLElement>('.p-time-slots__column');
      slotCols.forEach((slotCol) => {
        const slotHeader = slotCol.querySelector<HTMLElement>('.p-time-slots__header');
        if (slotHeader) {
          const clone = slotHeader.cloneNode(true) as HTMLElement;
          clone.style.position = 'fixed';
          clone.style.visibility = 'hidden';
          clone.style.zIndex = '40';
          clone.style.margin = '0';
          clone.setAttribute('aria-hidden', 'true');
          document.body.appendChild(clone);

          stickyItems.push({
            type: 'slot',
            original: slotHeader,
            clone,
            container: slotCol,
            parentDayHeader: dayHeader,
          });
        }
      });
    });

    // 2. スクロール時の追従処理
    const updateHeaders = () => {
      // トラッカーの位置を取得し、ChromeのURLバーによる「安全な上部(safeTop)」を動的に計算する
      const safeTrackerRect = safeTracker.getBoundingClientRect();
      const safeTop = Math.max(0, safeTrackerRect.top);

      stickyItems.forEach((item) => {
        const { type, original, clone, container, parentDayHeader } = item;

        // 本物が非表示（07/04のような空の列）の場合はクローンも隠してスキップ
        if (original.offsetWidth === 0) {
          clone.style.visibility = 'hidden';
          return;
        }

        // 本物の要素と親カラムの現在位置を取得
        const containerRect = container.getBoundingClientRect();
        const origRect = original.getBoundingClientRect();
        const { height, width } = origRect;

        let isSticky = false;
        let topPos = 0;

        if (type === 'day') {
          // 画面上部（安全圏）に到達したら固定
          if (containerRect.top < safeTop && containerRect.bottom > safeTop) {
            isSticky = true;
            topPos = safeTop; // URLバーの下に配置

            // 下から押し上げられる処理
            if (containerRect.bottom < safeTop + height) {
              topPos = containerRect.bottom - height;
            }
          }
        } else if (type === 'slot') {
          // 日付ヘッダーのすぐ下（安全圏）に到達したら固定
          const dayHeight = parentDayHeader && parentDayHeader.offsetWidth > 0 ? parentDayHeader.offsetHeight : 0;
          const slotSafeTop = safeTop + dayHeight; // URLバー高さ ＋ 日付ヘッダー高さ

          if (containerRect.top < slotSafeTop && containerRect.bottom > slotSafeTop) {
            isSticky = true;
            topPos = slotSafeTop;

            // 下から押し上げられる処理
            if (containerRect.bottom < slotSafeTop + height) {
              topPos = containerRect.bottom - height;
            }
          }
        }

        // クローンの位置と表示を更新
        if (isSticky) {
          clone.style.visibility = 'visible';
          clone.style.top = `${topPos}px`;
          clone.style.left = `${origRect.left}px`;
          clone.style.width = `${width}px`;
          clone.style.height = `${height}px`;
        } else {
          clone.style.visibility = 'hidden';
        }
      });
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateHeaders();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    if (scrollArea) {
      scrollArea.addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('resize', onScroll, { passive: true });

    // 初始执行一次
    updateHeaders();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (scrollArea) {
        scrollArea.removeEventListener('scroll', onScroll);
      }
      // 清理创建的克隆元素
      stickyItems.forEach((item) => {
        if (item.clone && item.clone.parentNode) {
          item.clone.parentNode.removeChild(item.clone);
        }
      });
      // 清理安全追踪器
      if (safeTracker && safeTracker.parentNode) {
        safeTracker.parentNode.removeChild(safeTracker);
      }
    };
  }, [isGetScheduleLoaded, getSchedule]); // 当数据变化时重新绑定逻辑

  return (
    <>
      <Header />
      <CustomLoading isLoading={isGetReservationConfirmLoading} heightSize={50} isOverall />
      <div>
        <section className="p-store-info schedule p-store-info--padding-y10">
          <div className="p-store-info__container">
            <div className="p-store-info__image-wrapper">
              <picture className="p-store-info__picture">
                <img className="p-store-info__image" src={`${shop?.image}`} alt="" />
              </picture>
            </div>

            <div className="p-store-info__details">
              <div className="p-store-info__title-box">
                <div className="p-store-info__title">{shop?.name}</div>
                <button type="button" className="p-store-info__icon-link u-pc-only" onClick={backToShopPage}>
                  他の店舗を選び直す
                </button>
              </div>
              <div className="p-store-info__main">
                <div className="p-store-info__image-wrapper p-store-info__image-wrapper--sp">
                  <picture className="p-store-info__picture">
                    <img className="p-store-info__image" src={`${shop?.image}`} alt="" />
                  </picture>
                </div>

                <div className="p-store-info__list">
                  <div className="p-store-info__list-item" style={{ whiteSpace: 'pre-line' }}>
                    <div className="p-store-info__label">営業時間</div>
                    <div className="p-store-info__text">
                      {shop && shop.businessHours && shop.businessHours.replace(/\n+/g, '\n')}
                    </div>
                  </div>
                  <div className="p-store-info__list-item">
                    <div className="p-store-info__label">電話番号</div>
                    <a
                      href={`tel:${shop && shop.phone ? shop.phone.replace(/-/g, '') : ''}`}
                      className="p-store-info__text p-store-info__text--tel-link"
                    >
                      {shop?.phone}
                    </a>
                  </div>
                  <div className="p-store-info__list-item">
                    <div className="p-store-info__label">住所</div>
                    <div className="p-store-info__text">{shop?.address}</div>
                  </div>
                  {shop && shop?.annexOpenDate ? (
                    <div className="p-store-info__list-item">
                      <div className="p-store-info__label">別館営業時間</div>
                      <div className="p-store-info__text">{shop?.annexOpenDate}</div>
                    </div>
                  ) : (
                    <></>
                  )}
                  {shop && shop?.annexAddress ? (
                    <div className="p-store-info__list-item">
                      <div className="p-store-info__label">別館住所</div>
                      <div className="p-store-info__text">{shop?.annexAddress}</div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              <div className="p-store-info__icons">
                {shop?.features.map((featuresInfo, index) => (
                  <div className="p-feature-tag--white-box" key={featuresInfo.id}>
                    <picture className="p-feature-tag__picture">
                      <img loading="lazy" className="p-feature-tag__image" src={featuresInfo.icon} alt="" />
                    </picture>
                  </div>
                ))}
              </div>

              <div className="p-store-info__sp-button-area">
                <button type="button" className="p-store-info__icon-link" onClick={backToShopPage}>
                  他の店舗を選び直す
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div>
        {/* コンテナ内パーツ */}
        <div className="css-fcftey schedulePage schedule">
          <section className="p-title-area p-title-area--padding-y30">
            <p className="c-title u-text-center">
              <>体験・見学するプログラムと日時を選択してください</>
            </p>
          </section>
          <section className="p-content-area u-gap30 u-pb16">
            <div className="p-content-area__column p-content-area__column--width1360 u-gap30 u-sp-gap0">
              {/* 共有パーツ カテゴリーコンテナ(パソコン用) */}
              <div className="p-category-button u-pc-only">
                {getSchedule.programList.map((category, index) => (
                  <React.Fragment key={`category-${category.programId}`}>
                    <input
                      type="radio"
                      name="category"
                      id={`category-${index}`}
                      className="p-category-button__slot"
                      checked={category.selectFlag === 1}
                      value={category.programId}
                      onChange={onChangeProgramId}
                    />
                    <label htmlFor={`category-${index}`} className="p-category-button__label">
                      {category.programName}
                    </label>
                  </React.Fragment>
                ))}
              </div>
              {/* / 共有パーツ カテゴリーコンテナ(パソコン用) */}

              {/* 共有パーツ カテゴリーコンテナ(モバイル用) */}
              <div className="c-select u-sp-only">
                {getSchedule.programList && getSchedule.programList.length > 0 ? (
                  <select
                    name="store"
                    className="c-select__window"
                    onChange={onChangeSelectProgramId}
                    value={queryParam.programId || ''}
                  >
                    {getSchedule.programList.map((category, index) => (
                      <option key={`category-${category.programId}`} value={category.programId}>
                        {category.programName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select name="store" className="c-select__window">
                    <option value="">予約可能プログラム存在しません</option>
                  </select>
                )}
              </div>
              {/* / 共有パーツ カテゴリーコンテナ(モバイル用) */}

              {programComment ? (
                <div className="p-expand-container">
                  <input type="checkbox" id="toggle-1" className="p-expand-container__input" />
                  <div className="p-expand-container__content" style={{ whiteSpace: 'pre-line' }}>
                    {programComment}
                  </div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="toggle-1" className="p-expand-container__label" />
                </div>
              ) : (
                <></>
              )}
            </div>

            {/* 共有パーツ ボタングループ */}
            <div className="p-button-group p-button-group--flex-end p-button-group--sp-center">
              <button
                type="button"
                className="p-schedule-button c-button c-button--primary c-button--small c-button--prev u-gap10"
                aria-label="前の週へ"
                onClick={() => changeSearchDay(2)}
                disabled={queryParam.imageDate !== null && queryParam.imageDate <= preOpenDay}
              >
                前の週へ
              </button>
              <button
                type="button"
                className="p-schedule-button p-schedule-button--small c-button c-button--primary c-button--small"
                aria-label="今日"
                onClick={() => changeSearchDay(1)}
              >
                今日
              </button>
              <button
                type="button"
                className="p-schedule-button c-button c-button--primary c-button--small c-button--next u-gap10"
                aria-label="次の週へ"
                onClick={() => changeSearchDay(3)}
              >
                次の週へ
              </button>
            </div>
            {/* / 共有パーツ ボタングループ */}
          </section>
        </div>

        <div
          className={`p-scroll-sticky-area ${isAllNoData ? 'p-scroll-sticky-area--height-auto' : ''}`}
          ref={scrollContainerRef}
        >
          <div className="p-schedule-grid">
            {getSchedule.scheduleList.map((day, dayIndex) => (
              <div
                key={day.infoDate}
                className={`p-schedule-grid__day-column ${
                  dayIndex === getSchedule.scheduleList.length - 1 ? 'p-schedule-grid__day-column--border-right' : ''
                } 
                ${!day.contentsList || day.contentsList.length === 0 ? 'p-schedule-grid__day-column--pink-gray' : ''}
                `}
              >
                <div
                  className={`p-schedule-grid__day-header ${
                    day.week === '土' ? 'p-schedule-grid__day-header--blue' : ''
                  } ${day.week === '日' ? 'p-schedule-grid__day-header--red' : ''}
                    `}
                >
                  {formatShowDate(day.infoDate)}({day.week})
                </div>
                <div
                  className={`p-time-slots ${
                    !day.contentsList || day.contentsList.length === 0 ? 'p-time-slots--none' : ''
                  }`}
                >
                  {day.contentsList && day.contentsList.length > 0 ? (
                    day.contentsList.map((service, serviceIndex) => (
                      <div
                        key={`column ${day.infoDate}-service-${service.contentsId}`}
                        className="p-time-slots__column"
                      >
                        <div className="p-time-slots__header">
                          <div className="p-time-slots__title">{service.contentsName}</div>
                          <div className="p-time-slots__tool">
                            {service.isAnnex === 1 && (
                              <span className="c-genre-label c-genre-label--large-pc">別館</span>
                            )}
                          </div>
                        </div>
                        <div className="p-time-slots__main">
                          {service.frameList && service.frameList.length > 0 ? (
                            service.frameList.map((slot, slotIndex) => (
                              <button
                                key={`${day.infoDate}-${service.contentsId}-${slot.startTime}`}
                                type="button"
                                className={`p-schedule-item ${
                                  slot.bookedCount === slot.totalCount ? 'p-schedule-item--shadow-none' : ''
                                }`}
                                onClick={() => {
                                  openReservationPop(
                                    day.infoDate,
                                    day.week,
                                    service,
                                    slot,
                                    shop?.name || '',
                                    slot.isDisabled,
                                    0,
                                    0,
                                  );
                                }}
                              >
                                <div
                                  className={`p-schedule-item__time ${
                                    slot.isDisabled || slot.bookedCount === slot.totalCount
                                      ? 'p-schedule-item__time--gray'
                                      : ''
                                  }`}
                                >
                                  {formatShowTime(slot.startTime)}~{formatShowTime(slot.endTime)}
                                </div>
                                <div className="p-schedule-item__group">
                                  {slot.bookedCount === slot.totalCount ? (
                                    <span className="p-schedule-item__sold-out">FULL</span>
                                  ) : (
                                    <span
                                      className={`p-schedule-item__number ${
                                        slot.isDisabled ? 'p-schedule-item__time--gray' : ''
                                      }`}
                                    >
                                      {slot.bookedCount}/{slot.totalCount}
                                    </span>
                                  )}
                                  <span
                                    className={`p-schedule-item__text ${
                                      slot.isDisabled ? 'p-schedule-item__time--gray' : ''
                                    }`}
                                  >
                                    詳細
                                    <img
                                      src={
                                        slot.isDisabled
                                          ? '/images/icons/icon-arrow-right-gray.svg'
                                          : '/images/icons/icon-arrow-right-red.svg'
                                      }
                                      alt="画像"
                                      className="p-schedule-item__icon"
                                    />
                                  </span>
                                </div>
                              </button>
                            ))
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-time-slots__column">
                      <div className="p-time-slots__header">
                        <div className="p-time-slots__title">
                          <></>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="p-blank-space" />
          </div>
        </div>

        <ScheduleReservationPop
          modalShow={reservationPopModal}
          onModalShow={() => {
            setReservationPopModal(false);
          }}
          onRefresh={searchSchedule}
        />
      </div>

      <div>
        <section className="p-button-area p-sticky__button-area">
          <button
            type="button"
            className="c-button c-button--secondary c-button--large c-button--prev-gray"
            onClick={forwardBack}
          >
            戻る
          </button>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Schedule;
